import axios from 'axios';
import { buildAuthHeaders, readAuthContext } from '@/services/api/_authHeaders';
import { CHANGE_PASSWORD_PATH } from '@/router/passwordChangeRoute';

// Get API base URL from environment variable, fallback to relative path for development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attaches Authorization and X-Hospital-Id headers.
axiosInstance.interceptors.request.use(
  (config) => {
    const authHeaders = buildAuthHeaders();
    for (const [key, value] of Object.entries(authHeaders)) {
      config.headers[key] = value;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * Stores the tokens a refresh just issued.
 *
 * localStorage first and unconditionally: it is what `buildAuthHeaders` reads,
 * so the next request must see the new token even if Pinia is not up yet. The
 * store is then brought into step because it is not merely a cache — `logout`
 * revokes whatever refresh token it holds, and rotation has already retired the
 * previous one, so a stale store would revoke a dead token and leave the live
 * one usable for its full 30-day life. `useQueueSocket` reads the access token
 * from the store too.
 */
async function persistRefreshedTokens(accessToken: string, newRefreshToken: string): Promise<void> {
  const stored = readAuthContext() ?? {};
  localStorage.setItem('auth', JSON.stringify({ ...stored, token: accessToken, refreshToken: newRefreshToken }));

  try {
    // Imported lazily: the auth store imports this module, so a static import
    // would be a cycle. By call time both modules are loaded.
    const { useAuthStore } = await import('@/stores/auth');
    useAuthStore().applyRefreshedTokens(accessToken, newRefreshToken);
  } catch {
    // No active Pinia (a refresh that beat app mount). localStorage above
    // already carries the new tokens and rehydration will pick them up.
  }
}

let refreshInFlight: Promise<string | null> | null = null;

async function requestNewTokens(): Promise<string | null> {
  const stored = readAuthContext();
  if (!stored?.refreshToken) return null;

  // Bare axios on purpose — routing this through `axiosInstance` would put the
  // refresh call under the very interceptor that issued it.
  const response = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken: stored.refreshToken });
  const { accessToken, refreshToken: newRefreshToken } = response.data;
  await persistRefreshedTokens(accessToken, newRefreshToken);
  return accessToken;
}

/**
 * Refreshes the session, at most once at a time. Rotation (F-07) revokes the
 * refresh token it is handed, so parallel 401s each refreshing on their own
 * would send the same token twice and every loser would be rejected — logging
 * a perfectly good session out. Callers share the one in-flight attempt.
 *
 * Resolves to the new access token, or null when there is nothing to refresh
 * with. Rejects if the refresh itself was refused.
 */
export function refreshAuthTokens(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = requestNewTokens().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// The JWT guard refuses a missing, invalid or expired access token with an UnauthorizedException, which the API's
// exception filter codes UNAUTHORIZED (apps/backend/.../guards/jwt-auth.guard.ts). Every other 401 carries the API's own
// code for a business answer that a new access token cannot change: a wrong current password or 2FA code, an expired
// 2FA challenge, reset link or portal invitation.
const ACCESS_TOKEN_REFUSED_CODE = 'UNAUTHORIZED';

// The JWT guard's 403 while the account must change a password someone else chose.
const PASSWORD_CHANGE_REQUIRED_CODE = 'PASSWORD_CHANGE_REQUIRED';

function apiCodeOf(error: { response?: { data?: unknown } }): unknown {
  const body = error.response?.data;
  return body && typeof body === 'object' ? (body as { code?: unknown }).code : undefined;
}

/** Whether a 401 refuses the access token; one with no API code (not shaped by the API) is treated as such. */
function refusesAccessToken(error: { response?: { data?: unknown } }): boolean {
  const code = apiCodeOf(error);
  return code === undefined || code === ACCESS_TOKEN_REFUSED_CODE;
}

function redirectToLogin(): void {
  localStorage.removeItem('auth');
  window.location.href = '/login';
}

/** Keeps the session but marks it, in storage first as `persistRefreshedTokens` does, so the router holds it on the change page. */
async function redirectToPasswordChange(): Promise<void> {
  localStorage.setItem('auth', JSON.stringify({ ...(readAuthContext() ?? {}), mustChangePassword: true }));
  try {
    const { useAuthStore } = await import('@/stores/auth');
    useAuthStore().mustChangePassword = true;
  } catch {
    // No active Pinia; rehydration reads the flag from localStorage.
  }
  if (window.location.pathname !== CHANGE_PASSWORD_PATH) {
    window.location.href = CHANGE_PASSWORD_PATH;
  }
}

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Login and refresh never go through the refresh-or-redirect branch: the login form renders "wrong password"
    // inline, and a failing refresh must not trigger another refresh.
    const requestUrl: string = originalRequest?.url ?? '';
    const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh');

    // A refused access token: refresh it once and replay the request.
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint && refusesAccessToken(error)) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAuthTokens();

        if (!accessToken) {
          // Nothing to refresh with — the session is over.
          redirectToLogin();
          return Promise.reject(error);
        }

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 403 && apiCodeOf(error) === PASSWORD_CHANGE_REQUIRED_CODE) {
      await redirectToPasswordChange();
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
export { API_BASE_URL };
